
import { useState } from "react";
import { useSession } from "@/hooks/useSession";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { ResourceFormData } from "@/components/resource/types";
import { useQueryClient } from "@tanstack/react-query";

export function useResourceCreation() {
  const { session } = useSession();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const createResource = async (data: ResourceFormData) => {
    if (!session?.user) {
      toast({
        title: "Error",
        description: "You must be logged in to create resources",
        variant: "destructive",
      });
      return false;
    }

    setIsLoading(true);
    try {
      let logoUrl = null;
      
      if (data.logo && data.logo[0]) {
        const file = data.logo[0];
        const fileExt = file.name.split('.').pop();
        const fileName = `${Math.random()}.${fileExt}`;
        const { error: uploadError, data: uploadData } = await supabase.storage
          .from('compliance-2')
          .upload(fileName, file);

        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('compliance-2')
          .getPublicUrl(fileName);
          
        logoUrl = publicUrl;
      }

      // First create the resource
      const { data: resource, error } = await supabase
        .from('resources')
        .insert({
          user_id: session.user.id,
          topic: data.topic,
          primary_color: data.primaryColor,
          secondary_color: data.secondaryColor,
          logo_url: logoUrl,
          company: data.company,
          company_info: data.companyInfo,
          additional_info: data.additionalInfo,
        })
        .select()
        .single();

      if (error) throw error;

      // Create initial file
      const { data: file, error: fileError } = await supabase
        .from('resource_files')
        .insert({
          resource_id: resource.id,
          title: 'Introduction',
          description: 'Welcome to this training resource',
          file_url: data.file_url || '',
          order_index: 0
        })
        .select()
        .single();

      if (fileError) throw fileError;

      queryClient.invalidateQueries({ queryKey: ['resources'] });
      queryClient.invalidateQueries({ queryKey: ['profile'] });

      toast({
        title: "Success",
        description: "Resource created successfully",
      });
      
      return true;
    } catch (error) {
      console.error('Error creating resource:', error);
      toast({
        title: "Error",
        description: "Failed to create resource. Please try again.",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return { createResource, isLoading };
}
